// src/pages/brand-collaborations-client-stories/components/InteractiveTimeline.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const InteractiveTimeline = () => {
  const [activeYear, setActiveYear] = useState(2023);

  const timelineData = [
    {
      year: 2019,
      title: "Foundation & First Partnerships",
      subtitle: "Building Trust Through Excellence",
      partnerships: [
        {
          client: "TechCorp Industries",
          project: "Product Launch Campaign",
          impact: "300% engagement increase",
          image: "https://images.pixabay.com/photo/2017/07/31/11/21/people-2557396_960_720.jpg"
        }
      ],
      metrics: { projects: 12, clients: 8, satisfaction: "95%" }
    },
    {
      year: 2020,
      title: "Digital Transformation Era",
      subtitle: "Adapting to New Realities",
      partnerships: [
        {
          client: "Luxury Properties Global",
          project: "Virtual Property Tours",
          impact: "500% increase in virtual viewings",
          image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop"
        },
        {
          client: "Global Events Co.",
          project: "Hybrid Event Documentation",
          impact: "250% reach expansion",
          image: "https://images.pexels.com/photos/1709003/pexels-photo-1709003.jpeg?auto=compress&cs=tinysrgb&w=1600"
        }
      ],
      metrics: { projects: 25, clients: 18, satisfaction: "97%" }
    },
    {
      year: 2021,
      title: "Strategic Expansion",
      subtitle: "Fortune 500 Recognition",
      partnerships: [
        {
          client: "Fortune 500 Tech Giant",
          project: "Brand Identity Campaign",
          impact: "450% brand perception lift",
          image: "https://images.pixabay.com/photo/2016/02/13/12/26/aurora-1197753_960_720.jpg"
        },
        {
          client: "International Real Estate",
          project: "Luxury Development Series",
          impact: "200% sales conversion",
          image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?q=80&w=2070&auto=format&fit=crop"
        }
      ],
      metrics: { projects: 45, clients: 32, satisfaction: "98%" }
    },
    {
      year: 2022,
      title: "Innovation Leadership",
      subtitle: "Setting Industry Standards",
      partnerships: [
        {
          client: "Global Automotive Leader",
          project: "Next-Gen Vehicle Launch",
          impact: "600% engagement metrics",
          image: "https://images.pexels.com/photos/3802510/pexels-photo-3802510.jpeg?auto=compress&cs=tinysrgb&w=1600"
        },
        {
          client: "Luxury Hospitality Chain",
          project: "Brand Experience Campaign",
          impact: "350% booking increase",
          image: "https://images.pixabay.com/photo/2018/11/23/15/10/buildings-3834138_960_720.jpg"
        }
      ],
      metrics: { projects: 65, clients: 45, satisfaction: "99%" }
    },
    {
      year: 2023,
      title: "Excellence Ecosystem",
      subtitle: "Comprehensive Solutions",
      partnerships: [
        {
          client: "Multinational Corporation",
          project: "Global Brand Transformation",
          impact: "800% market penetration",
          image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=2070&auto=format&fit=crop"
        },
        {
          client: "Tech Innovation Hub",
          project: "Future Vision Campaign",
          impact: "500% investor interest",
          image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1600"
        }
      ],
      metrics: { projects: 85, clients: 60, satisfaction: "99.5%" }
    }
  ];

  const currentData = timelineData.find(item => item.year === activeYear);

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            Partnership <span className="text-gradient-gold">Evolution</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Navigate through our journey of strategic brand partnerships, showcasing growth, 
            innovation, and measurable impact across diverse industry sectors
          </p>
        </div>

        {/* Timeline Navigation */}
        <div className="flex items-center justify-center mb-12">
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute top-6 left-0 right-0 h-0.5 bg-border-default"></div>
            <div className="absolute top-6 left-0 h-0.5 bg-accent" style={{ width: `${((activeYear - 2019) / 4) * 100}%` }}></div>
            
            {/* Timeline Points */}
            <div className="flex items-center space-x-16">
              {timelineData.map((item) => (
                <button
                  key={item.year}
                  onClick={() => setActiveYear(item.year)}
                  className={`relative z-10 w-12 h-12 rounded-full border-4 transition-all duration-300 ${
                    activeYear === item.year
                      ? 'bg-accent border-accent shadow-glow'
                      : 'bg-white border-border-default hover:border-accent'
                  }`}
                >
                  <span className={`block text-sm font-bold mt-16 transition-colors duration-300 ${
                    activeYear === item.year ? 'text-accent' : 'text-text-secondary'
                  }`}>
                    {item.year}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Active Year Content */}
        {currentData && (
          <div className="animate-fade-in">
            {/* Year Header */}
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-text-primary mb-2">{currentData.title}</h3>
              <p className="text-lg text-text-secondary">{currentData.subtitle}</p>
            </div>

            {/* Partnerships Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {currentData.partnerships.map((partnership, index) => (
                <div key={index} className="bg-white rounded-lg overflow-hidden shadow-subtle hover:shadow-elevation transition-all duration-300">
                  <div className="relative h-48">
                    <Image
                      src={partnership.image}
                      alt={partnership.project}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h4 className="text-white font-semibold text-lg mb-1">{partnership.client}</h4>
                      <p className="text-white/80 text-sm">{partnership.project}</p>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-2 text-accent font-semibold">
                      <Icon name="TrendingUp" size={20} strokeWidth={2} />
                      <span>{partnership.impact}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Year Metrics */}
            <div className="grid grid-cols-3 gap-8 bg-white rounded-lg p-8 shadow-subtle">
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">{currentData.metrics.projects}</div>
                <div className="text-text-secondary">Projects Completed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">{currentData.metrics.clients}</div>
                <div className="text-text-secondary">Active Clients</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">{currentData.metrics.satisfaction}</div>
                <div className="text-text-secondary">Client Satisfaction</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default InteractiveTimeline;